/**
 * Render-time richtext sanitizer — isomorphic (Astro SSR under Node AND the browser).
 *
 * This is the live PUBLIC XSS barrier. The rich-text section component (rendered by the shared
 * PageRenderer, which the storefront SSRs through Astro) calls this BEFORE dangerouslySetInnerHTML, so a
 * payload tampered-at-rest (a curl-with-valid-JWT, a future server bug, a non-builder write path) is
 * neutralized on the storefront SSR path too — not only the builder write path.
 *
 * `isomorphic-dompurify` provides DOMPurify in the browser and a jsdom-backed DOMPurify under Node, fed
 * the exact same RICHTEXT_ALLOW policy the builder's write-time sanitizer uses (single source of truth).
 *
 * The policy's `ALLOWED_URI_REGEXP` is enforced via an `uponSanitizeAttribute` hook scoped to `href`
 * (http/https/mailto/relative; rejects javascript:/data:). It is NOT passed to DOMPurify directly because
 * that applies it to EVERY allowed attribute (it would strip `target`/`rel`). DOMPurify's secure default
 * already blocks dangerous schemes; the hook tightens it to exactly our allowlist.
 *
 * An `afterSanitizeAttributes` hook forces `rel="noopener noreferrer"` on any `target=_blank` link — the
 * SAME reverse-tabnabbing defense the builder's write-time sanitizer applies, mirrored here so a link
 * tampered-at-rest (a curl-with-valid-JWT write, a future server bug) is hardened on the render path too,
 * not only when the stored HTML happens to already carry `rel`.
 */
import DOMPurify from 'isomorphic-dompurify';
import { RICHTEXT_ALLOW, sanitizeStyleAttribute } from './richtextAllow.js';
let hookInstalled = false;
function ensureHook() {
    if (hookInstalled)
        return;
    DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
        // Hold both link `href` and image `src` to the URL allowlist (http(s)/root-relative only) — a
        // javascript:/data: URL on either is dropped. Event handlers never reach here (not in ALLOWED_ATTR).
        if ((data.attrName === 'href' || data.attrName === 'src') &&
            !RICHTEXT_ALLOW.ALLOWED_URI_REGEXP.test(data.attrValue)) {
            data.keepAttr = false;
        }
        // `style` crosses only as the four properties the policy names, rewritten from the parsed
        // declarations — never as the seller authored it. Nothing left to keep → drop the attribute rather
        // than leave an empty `style=""` behind.
        if (data.attrName === 'style') {
            const clean = sanitizeStyleAttribute(data.attrValue);
            if (clean)
                data.attrValue = clean;
            else
                data.keepAttr = false;
        }
    });
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        // Parity with the write-time sanitizer: force rel=noopener noreferrer on any target=_blank link.
        if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
    hookInstalled = true;
}
/**
 * Memo of past results, keyed by the EXACT input string. Sound because this function is pure: the
 * policy is a module constant, so one input always yields one output — a cached answer is the answer
 * the sanitizer would have recomputed. Keying on the full input is also what keeps it tenant-safe:
 * a hit requires byte-identical HTML, so one seller's markup can never surface on another's page.
 *
 * It exists because under Node (Astro SSR) `DOMPurify.sanitize` builds a fresh jsdom `Document` per
 * call, and those documents are NOT reclaimed promptly — a storefront page makes ~70 calls over 30
 * sections with the SAME theme strings on every visit, so without this each visitor re-parsed the
 * whole theme and left another pile of documents behind. Measured on preprod 2026-10-06: a product
 * page went 348ms → 2.7s over 200 renders, and never recovered until the process restarted.
 *
 * Bounded two ways, because a cache that grows without limit is the bug it replaces: at most
 * [CACHE_MAX] entries (LRU — a hit moves the key back to the tail, an insert past the cap drops the
 * head), and inputs past [CACHE_MAX_INPUT] bytes are sanitized without ever being stored.
 */
const CACHE_MAX = 512;
const CACHE_MAX_INPUT = 256 * 1024;
const cache = new Map();
/**
 * Sanitize seller HTML against the shared allowlist. Returns a clean HTML string safe to inject.
 * A non-string input (defensive — a corrupted document) yields an empty string.
 */
export function sanitizeRichtextHtml(html) {
    if (typeof html !== 'string' || html.length === 0)
        return '';
    const cacheable = html.length <= CACHE_MAX_INPUT;
    if (cacheable) {
        const hit = cache.get(html);
        if (hit !== undefined) {
            // Refresh recency: delete + set moves the key to the tail of Map's insertion order.
            cache.delete(html);
            cache.set(html, hit);
            return hit;
        }
    }
    ensureHook();
    const clean = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [...RICHTEXT_ALLOW.ALLOWED_TAGS],
        ALLOWED_ATTR: [...RICHTEXT_ALLOW.ALLOWED_ATTR],
    });
    if (cacheable) {
        cache.set(html, clean);
        if (cache.size > CACHE_MAX) {
            // Map iterates in insertion order, so the first key is the least recently used.
            const oldest = cache.keys().next();
            if (!oldest.done)
                cache.delete(oldest.value);
        }
    }
    return clean;
}
/** Test seam — drops every memoized result so a case can observe a cold sanitize. */
export function __clearRichtextCacheForTests() {
    cache.clear();
}
//# sourceMappingURL=sanitizeRichtextHtml.js.map